import { useState, type ReactNode } from "react";
import type { RaceChartData } from "@shared/types";
import { Sheet, SHEET } from "../../components/Sheet";
import { carKey, carNo } from "./car-number";
import { classColor, type PitVisit } from "./RunningOrder";

/** Behind a chip floating over the map on a wide screen. */
const OVER_BG = "rgba(11,11,24,0.82)";
const C = { line: "#1e1e38", text: "#e0e0e8", muted: "#9a9ab4", chip: "#2a2a48", pit: "#2563eb", out: "#6b7280", focus: "#F48C34" };

/** Chart keys in running order (position now, then car number). */
export function inOrder(data: RaceChartData, keys: Iterable<number>): number[] {
  return [...keys].sort(
    (a, b) =>
      (data.cars[String(a)]?.finishPos || 1e6) - (data.cars[String(b)]?.finishPos || 1e6) ||
      carNo(data, a).localeCompare(carNo(data, b), undefined, { numeric: true })
  );
}

/**
 * RaceTrace Live, under the track map: the cars in the pits making a stop, and the cars not
 * running (OUT: in the garage, retired, parked), each in its class colour (floating over the map's
 * right edge on a wide screen). Tapping one makes it
 * the focus car.
 */
export function PitList({
  data,
  pits,
  out,
  focusNum,
  setFocusNum,
  className = "",
}: {
  data: RaceChartData;
  pits: Map<number, PitVisit>;
  out: Set<number>;
  focusNum: number;
  setFocusNum: (n: number) => void;
  /** Placement (the track map puts it beside the map on a wide screen). */
  className?: string;
}) {
  const stopping = inOrder(data, [...pits.keys()].filter((k) => !out.has(k)));
  const notRunning = inOrder(data, out);
  if (!stopping.length && !notRunning.length) return null;
  const chip = (key: number, dim: boolean) => {
    const focus = key === focusNum;
    return (
      <button
        key={key}
        type="button"
        onClick={() => setFocusNum(key)}
        className="pointer-events-auto min-h-[44px] w-fit rounded-md border px-2.5 font-bold tabular-nums hover:bg-white/5 sm:min-h-0 sm:px-1.5 sm:py-0.5 xl:backdrop-blur-sm"
        style={{
          background: OVER_BG,
          borderColor: focus ? C.focus : C.chip,
          borderLeft: `4px solid ${classColor(data.cars[String(key)]?.cls ?? "")}`,
          color: focus ? C.focus : dim ? C.muted : C.text,
        }}
      >
        #{carNo(data, key)}
      </button>
    );
  };
  const tag = (text: string, bg: string) => (
    <span className="rounded px-1 text-[10px] font-bold text-white" style={{ background: bg }}>
      {text}
    </span>
  );
  // One row under the map: the cars in the pits, then the cars not running (wrapping on a narrow
  // screen). On a wide screen: a single column of chips floating over the map's right edge, each
  // group under its tag and count; its rows keep their height and it scrolls when long, stopping
  // above the turn buttons (the map is fitted beside it, map-room.ts).
  return (
    <div
      data-map-overlay
      className={`custom-scroll flex flex-wrap items-center gap-1.5 border-t px-3 py-2 text-xs sm:px-6 xl:pointer-events-auto xl:absolute xl:right-2 xl:top-2 xl:max-h-[calc(100%-3.5rem)] xl:flex-col xl:flex-nowrap xl:items-end xl:overflow-y-auto xl:border-0 xl:p-0 xl:[&>*]:shrink-0 ${className}`}
      style={{ borderColor: C.line, color: C.muted }}
    >
      {stopping.length > 0 && (
        <>
          <span className="mr-1 flex items-center gap-1.5 font-semibold xl:mr-0 xl:rounded xl:px-1 xl:py-0.5" style={{ color: C.text, background: OVER_BG }} title="In the pits">
            {tag("PIT", C.pit)} <span className="xl:hidden">In the pits</span> ({stopping.length})
          </span>
          {stopping.map((k) => chip(k, false))}
        </>
      )}
      {stopping.length > 0 && notRunning.length > 0 && <span aria-hidden className="mx-1.5 h-4 w-px xl:hidden" style={{ background: C.chip }} />}
      {notRunning.length > 0 && (
        <span className="contents" title="Not running: no lap for over 15 minutes (retired, in the garage, parked)">
          <span className="mr-1 flex items-center gap-1.5 font-semibold xl:mr-0 xl:mt-2 xl:rounded xl:px-1 xl:py-0.5" style={{ color: C.text, background: OVER_BG }}>
            {tag("OUT", C.out)} <span className="xl:hidden">Not running</span> ({notRunning.length})
          </span>
          {notRunning.map((k) => chip(k, true))}
        </span>
      )}
    </div>
  );
}

/**
 * Phones, under the track map (mobile redesign): one slim row in place of the key's chips, the
 * pit and not-running chips and the following line, which took a screen of their own. Each count
 * opens a sheet: the cars in the pits, the cars not running (tap one to make it the focus car),
 * the map key (tap a layer to hide it), the cars followed (tap one to stop following it).
 *
 *   [PIT 5] [OUT 13]   [◐ Map key · 1 hidden]   [Following 2]
 */
export function PhoneMapBar({
  data,
  pits,
  out,
  focusNum,
  setFocusNum,
  followed,
  onFollow,
  mapKey,
  hiddenCount,
  notes,
}: {
  data: RaceChartData;
  pits: Map<number, PitVisit>;
  out: Set<number>;
  focusNum: number;
  setFocusNum: (n: number) => void;
  /** Car numbers followed (besides the focus car). */
  followed: string[];
  onFollow: (cars: string[]) => void;
  /** The map key's layer chips, shown in their sheet. */
  mapKey: ReactNode;
  hiddenCount: number;
  /** What the map cannot place right now ("Stopped: #12"): a dot on Map key, the lines in its sheet. */
  notes: string[];
}) {
  const [open, setOpen] = useState<"pit" | "out" | "key" | "follow" | null>(null);
  const stopping = inOrder(data, [...pits.keys()].filter((k) => !out.has(k)));
  const notRunning = inOrder(data, out);
  const follows = followed.filter((n) => {
    const k = carKey(data, n);
    return k !== null && !!data.cars[String(k)] && k !== focusNum;
  });
  const close = () => setOpen(null);
  const pill = "flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[13px] font-semibold relative before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']";
  const tag = (text: string, bg: string) => (
    <span className="rounded px-1 text-[10px] font-bold text-white" style={{ background: bg }}>
      {text}
    </span>
  );
  const carRow = (k: number, note: string) => {
    const c = data.cars[String(k)];
    const focus = k === focusNum;
    return (
      <li key={k}>
        <button
          type="button"
          onClick={() => {
            setFocusNum(k);
            close();
          }}
          className="flex min-h-[48px] w-full items-center gap-2.5 rounded-lg px-2 text-left active:bg-white/5"
          style={{ borderLeft: `4px solid ${classColor(c?.cls ?? "")}`, background: focus ? "rgba(244,140,52,0.12)" : undefined }}
        >
          <span className="w-12 shrink-0 font-mono text-[15px] font-bold" style={{ color: focus ? C.focus : SHEET.text }}>
            #{carNo(data, k)}
          </span>
          <span className="min-w-0 flex-1 truncate text-[14px]" style={{ color: SHEET.text }}>
            {c?.team ?? ""}
          </span>
          <span className="shrink-0 text-[12px]" style={{ color: SHEET.muted }}>
            {note}
          </span>
        </button>
      </li>
    );
  };
  const lapOf = (k: number) => {
    const v = pits.get(k);
    return v ? `in after lap ${v.afterLap}` : "";
  };
  return (
    <>
      <div className="no-scrollbar flex h-12 shrink-0 items-center gap-2 overflow-x-auto border-t px-3" style={{ borderColor: C.line, color: C.muted }} data-phone-map="bar">
        {stopping.length > 0 && (
          <button type="button" data-touch-44 onClick={() => setOpen("pit")} className={pill} style={{ borderColor: C.chip, color: C.text }} aria-label={`In the pits: ${stopping.length}`} title="Cars in the pits now, making a stop: tap for the list">
            {tag("PIT", C.pit)} {stopping.length}
          </button>
        )}
        {notRunning.length > 0 && (
          <button type="button" data-touch-44 onClick={() => setOpen("out")} className={pill} style={{ borderColor: C.chip, color: C.text }} aria-label={`Not running: ${notRunning.length}`} title="Cars not running: no lap for over 15 minutes (retired, in the garage or parked). Tap for the list">
            {tag("OUT", C.out)} {notRunning.length}
          </button>
        )}
        <button type="button" data-touch-44 onClick={() => setOpen("key")} className={pill} style={{ borderColor: C.chip, color: C.text }} title="What the map's marks mean, and which to show or hide">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5" />
          </svg>
          Map key
          {hiddenCount > 0 && <span style={{ color: C.muted }}>· {hiddenCount} hidden</span>}
          {notes.length > 0 && <span aria-label="notes" className="h-2 w-2 rounded-full" style={{ background: "#fab219" }} />}
        </button>
        {follows.length > 0 && (
          <button type="button" data-touch-44 onClick={() => setOpen("follow")} className={pill} style={{ borderColor: C.chip, color: C.text }} title="The cars you follow, drawn large on the map: tap to stop following one">
            Following {follows.length}
          </button>
        )}
      </div>

      <Sheet open={open === "pit"} onClose={close} title={`In the pits (${stopping.length})`}>
        <ul className="m-0 flex list-none flex-col gap-1 p-0" data-phone-map="pits">
          {stopping.map((k) => carRow(k, lapOf(k)))}
        </ul>
        <p className="m-0 mt-3 text-[12px]" style={{ color: SHEET.muted }}>Tap a car to make it the focus car.</p>
      </Sheet>
      <Sheet open={open === "out"} onClose={close} title={`Not running (${notRunning.length})`}>
        <ul className="m-0 flex list-none flex-col gap-1 p-0">{notRunning.map((k) => carRow(k, ""))}</ul>
        <p className="m-0 mt-3 text-[12px]" style={{ color: SHEET.muted }}>
          No lap for over 15 minutes: retired, in the garage or parked. Tap a car to make it the focus car.
        </p>
      </Sheet>
      <Sheet open={open === "key"} onClose={close} title="Map key">
        <p className="m-0 mb-2 text-[13px]" style={{ color: SHEET.muted }}>Tap one to hide it on the map, or show it again.</p>
        <div data-phone-map="key">{mapKey}</div>
        {notes.length > 0 && (
          <ul className="m-0 mt-3 list-none p-0 text-[13px]" style={{ color: SHEET.text }}>
            {notes.map((t) => (
              <li key={t} className="py-1">
                {t}
              </li>
            ))}
          </ul>
        )}
        <p className="m-0 mt-3 text-[12px]" style={{ color: SHEET.muted }}>Tap a car on the map to follow it.</p>
      </Sheet>
      <Sheet open={open === "follow"} onClose={close} title="Following">
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {follows.map((n) => {
            const k = carKey(data, n)!;
            return (
              <li key={n} className="flex min-h-[48px] items-center gap-2.5 px-2" style={{ borderLeft: `4px solid ${classColor(data.cars[String(k)]?.cls ?? "")}` }}>
                <span className="w-12 shrink-0 font-mono text-[15px] font-bold">#{n}</span>
                <span className="min-w-0 flex-1 truncate text-[14px]">{data.cars[String(k)]?.team ?? ""}</span>
                <button type="button" onClick={() => onFollow(followed.filter((c) => c !== n))} className="min-h-[44px] px-2 text-[13px] font-semibold" style={{ color: C.focus }}>
                  Stop
                </button>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          onClick={() => {
            onFollow([]);
            close();
          }}
          className="mt-3 min-h-[44px] text-[14px] font-semibold"
          style={{ color: C.focus }}
        >
          Stop following all
        </button>
      </Sheet>
    </>
  );
}
